import { getPlatformDisplayName } from 'src/utils/platform-label';

/**
 * Turns a raw error from a platform API call (or the Chrome extension) into a plain-English
 * shape a seller can act on: { title, message, suggestion, wayForward, retryable, fixUrl? }.
 *
 * `retryable` drives whether the bulk cross-listing UI offers a Retry button. Most rejections
 * are retryable (wrong category, rate limit, timeout); a few are not because retrying without
 * leaving this flow cannot fix them — the listing itself is missing something (package weight,
 * images) that can only be added from the listing edit form, or nothing the seller changes will
 * help at all.
 *
 * `wayForward` says what the seller does once they have followed the suggestion, which decides
 * where the suggestion can be shown:
 *   'retry'               - they try the item again: the advice names what to change first, or says
 *                           to retry outright. Almost every row. A screen without a Retry button
 *                           replaces or drops this advice and says in its own words how to try again.
 *   'contactVoolist'      - nothing the seller changes helps until Voolist does something, and the
 *                           advice says to tell us. Shown with the reason wherever the failure is.
 *   'finishOnMarketplace' - the item is finished on the marketplace instead of being tried again. It
 *                           is never replaced by an invitation to try again and never shown beside
 *                           one: a seller who did both would post the item twice.
 * It is declared on every row rather than read from the words, so rewording a suggestion cannot
 * silently move it; a test holds the declarations to the words they describe.
 *
 * Rows are checked in order and the first match wins, so more specific rows (e.g. a
 * platform+status combination) are listed before generic ones (the bare-status and
 * embedded-JSON fallbacks).
 */

// getPlatformDisplayName returns the platform's proper display name, or the raw platform string
// itself for one this project has no display name for. This wrapper adds only the empty-string
// case for a missing platform, since every call site below interpolates `label` straight into a
// sentence and can never use `undefined` there.
function platformLabel(platform) {
  return platform ? getPlatformDisplayName(platform) : '';
}

function extractJsonMessage(raw) {
  if (typeof raw !== 'string') return null;
  const objectMatch = raw.match(/\{[\s\S]*?"message"\s*:\s*"([^"]+)"/);
  if (objectMatch) return objectMatch[1];
  const arrayMatch = raw.match(/\[\s*\{[\s\S]*?"message"\s*:\s*"([^"]+)"/);
  if (arrayMatch) return arrayMatch[1];
  return null;
}

/**
 * Poshmark's wording when it refuses a listing because it was posted by automation rather than
 * typed in by hand: "We have detected that this item is suspicious. Please use our Single Listing
 * Creation form to list this item."
 *
 * Matched on the wording alone. The error TYPE travels in the same sentence, but `PostValidationError`
 * is what Poshmark also returns for an ordinary bad field, so branching on it would call a rejected
 * size an automation block. The wording is the only part that identifies this cause.
 *
 * These patterns say the same thing as the `blocked_as_automation` family in
 * server/src/utils/publish-failure-family.js, which is what counts and alerts on the same failures.
 * The two files cannot import each other, so a test calls the server's classifier with the same
 * strings and fails when they stop agreeing.
 */
const BLOCKED_AS_AUTOMATION_WORDING = [
  /is suspicious/i,
  /single listing (creation )?form/i,
  /cannot currently perform this request/i,
];

/**
 * Whether the marketplace refused this publish because we are automation.
 *
 * Nothing about the listing is wrong and nothing the seller changes in Voolist will help, which is
 * why this is asked in two places: here, to tell the seller the truth and withhold a Retry that
 * cannot work, and in the bulk runner, to stop a run before it spends a whole closet finding out.
 *
 * @param {string|Error} rawError
 * @returns {boolean}
 */
export function isBlockedAsAutomation(rawError) {
  const raw = rawError instanceof Error ? rawError.message : String(rawError || '');
  return BLOCKED_AS_AUTOMATION_WORDING.some(pattern => pattern.test(raw));
}

/**
 * The extension's sentence for a marketplace refusal caused by the seller's country: Voolist holds
 * no location for it, so the listing went out incomplete (FAILURE_FACT_MESSAGES.countryNotSupported
 * in chrome-extension/background/service-worker.js). Matched on our own words, so a marketplace's
 * sentence that merely mentions a country is never taken for it. The server files the same sentence
 * in server/src/utils/publish-failure-family.js, and a test runs both against it.
 */
const COUNTRY_NOT_SUPPORTED_WORDING = /voolist can.t post to .+ in your country yet/i;

/**
 * The extension's sentence for work it did not send because Chrome is signed in to a different account
 * on the marketplace than the one the seller connected (FAILURE_FACT_MESSAGES.otherAccount in
 * chrome-extension/background/service-worker.js). Matched on our own words. The server files the same
 * sentence with the signed-out causes in server/src/utils/publish-failure-family.js, and a test runs
 * both against it.
 */
const SIGNED_IN_TO_ANOTHER_ACCOUNT_WORDING = /signed in to a different .+ account/i;

/**
 * The web app's own words for an extension build too old to work on a marketplace
 * (updateExtensionMessage in extension-marketplace-support.js). Nothing was sent to the marketplace.
 */
const EXTENSION_TOO_OLD_WORDING = /update the voolist extension to use /i;

/**
 * Whether the extension sent nothing because Chrome is signed in to another account on the
 * marketplace than the one the seller connected — another Depop account, or another Poshmark closet
 * on the account's site.
 *
 * Asked here, to give the seller the row that says which account to sign in to, and in the bulk
 * runner, to answer the rest of that marketplace's items the same way instead of sending each one.
 *
 * @param {string|Error} rawError
 * @returns {boolean}
 */
export function isSignedInToAnotherAccount(rawError) {
  const raw = rawError instanceof Error ? rawError.message : String(rawError || '');
  return SIGNED_IN_TO_ANOTHER_ACCOUNT_WORDING.test(raw);
}

/**
 * Depop's own words for a listing that went out naming no shipping at all, which extensions older
 * than 1.1.42 pass on as they are. Only the key itself missing: a field missing inside one of its
 * methods reads `obj.shippingMethods[0]…`, and a shipping price would not fix that. The extension
 * (`readDepopRefusal`) and the server (publish-failure-family.js) match the same form.
 */
const DEPOP_SHIPPING_NOT_NAMED = /obj\.shippingMethods,\s*List\(JsonValidationError\(List\(error\.path\.missing\b/;

const RAW_TRUNCATE_LIMIT = 240;

function cleanRaw(raw) {
  if (typeof raw !== 'string') return '';
  // Strip a platform-generated prefix like "Depop create error: 422 - "
  let cleaned = raw.replace(/^\w+\s+\w+\s+error:\s*\d+\s*-\s*/i, '');
  if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
    try {
      const parsed = JSON.parse(cleaned);
      if (parsed && parsed.message) cleaned = parsed.message;
      else if (Array.isArray(parsed) && parsed[0]?.message) cleaned = parsed[0].message;
    } catch {
      /* not JSON — use the prefix-stripped text as-is */
    }
  }
  if (cleaned.length > RAW_TRUNCATE_LIMIT) {
    cleaned = `${cleaned.substring(0, RAW_TRUNCATE_LIMIT - 3)}...`;
  }
  return cleaned;
}

/**
 * Plain-English mapping for a raw error string (or Error) from the extension or a platform API.
 * @param {string|Error} rawError
 * @param {string} [platform]
 * @returns {{title:string, message:string, suggestion:string,
 *   wayForward:('retry'|'contactVoolist'|'finishOnMarketplace'), retryable:boolean, fixUrl?:string}}
 */
export function mapPlatformError(rawError, platform) {
  const raw = rawError instanceof Error ? rawError.message : String(rawError || '');
  const label = platformLabel(platform);
  const lower = raw.toLowerCase();

  // The installed extension is too old for this marketplace, so nothing was sent and nothing about
  // the listing or the account is wrong. Updating the extension is the whole fix.
  if (EXTENSION_TOO_OLD_WORDING.test(raw)) {
    return {
      title: 'Update the Voolist extension',
      message: `${label || 'This marketplace'} needs a newer version of the Voolist extension.`,
      suggestion: 'Update the Voolist extension, then try again.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  // A rejected token is the most specific thing a raw error can say: whatever else the message
  // contains (a timed-out photo check, a failed validation), the account has to be reconnected first.
  if (/(401|403)\b/.test(raw) || lower.includes('unauthor') || lower.includes('forbidden')) {
    return {
      title: `${label} account disconnected`,
      // Screens without a Retry button show this line alone, so it carries the fix.
      message: `Voolist can't reach your ${label ? `${label} ` : ''}account. Reconnect it on the Marketplaces page.`,
      suggestion: "Once it's reconnected, try again.",
      wayForward: 'retry',
      retryable: true,
      fixUrl: '/stores',
    };
  }

  // The extension publishes through the seller's own logged-in session on the marketplace site.
  // When that session is missing, the extension reports it in these words; the seller has to log
  // in on the marketplace, not reconnect anything in Voolist, so there is no fix link here.
  if (lower.includes('please log in to')) {
    return {
      title: `Log in to ${label || 'the marketplace'}`,
      message: `You’re not logged in to ${label || 'the marketplace'} in this browser.`,
      suggestion: `Open the ${label || 'marketplace'} website, log in, then click Retry.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // The session is there, but it is another account's: the extension sent nothing, because under that
  // login a create would go to the wrong shop. The seller signs in to the account they connected, on the
  // marketplace itself. An older connection can hold a wrong account, though, so a seller whose Chrome
  // is already on their own account is told to connect again. No fix link: the usual fix is on the
  // marketplace. The message names the account to use, because screens without a Retry button show it
  // without the suggestion.
  if (isSignedInToAnotherAccount(raw)) {
    const marketplace = label || 'the marketplace';
    return {
      title: `Signed in to another ${label || 'marketplace'} account`,
      message: `Chrome is signed in to a different ${label || 'marketplace'} account than the one you connected to Voolist.`,
      suggestion: `Open ${marketplace} and sign in to the account you connected to Voolist. `
        + `If you're already signed in to it, connect ${marketplace} again on the Marketplaces page.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // The seller took back the permission Voolist needs on the marketplace's site (Grailed's is optional,
  // asked for at connect), so nothing reached the marketplace. The marketplace's card in the extension's
  // popup asks for it again (Allow access). Matched on the extension's own sentence
  // (FAILURE_FACT_MESSAGES.accessMissing).
  if (lower.includes('needs your permission to work on')) {
    const marketplace = label || 'the marketplace';
    return {
      title: `Allow Voolist on ${marketplace}`,
      message: `Voolist needs your permission to work on ${marketplace}.`,
      suggestion: `Click the Voolist icon in Chrome's toolbar, then click Allow access on the ${marketplace} card. Then click Retry.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // Voolist holds none of the item, so nothing was posted: posting it would sell it twice. Adding
  // stock is the fix. The extension's own sentence (FAILURE_FACT_MESSAGES.noStock).
  if (lower.includes('is sold out in voolist')) {
    return {
      title: 'Sold out in Voolist',
      message: `This item is sold out, so it wasn't posted to ${label || 'the marketplace'}.`,
      suggestion: 'Add stock to the item, then try again.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  // The marketplace took the listing down itself (OfferUp's moderation) and emails the seller why. Nothing
  // Voolist sends puts it back, so this row offers no Retry. Matched on the extension's own sentence
  // (FAILURE_FACT_MESSAGES.removedByMarketplace).
  if (/removed this listing\. check your email from .+ for why/i.test(raw)) {
    const marketplace = label || 'the marketplace';
    return {
      title: `Removed by ${marketplace}`,
      message: `${label || 'The marketplace'} removed this listing. Check your email from ${marketplace} for why.`,
      suggestion: `Voolist won't post it to ${marketplace} again on its own.`,
      wayForward: 'finishOnMarketplace',
      retryable: false,
    };
  }

  // The marketplace could not read the request Voolist sent (OfferUp's `BAD_USER_INPUT`): Voolist built the
  // listing wrong, which only we can fix, so the seller is sent to us. Matched on the extension's own sentence
  // (FAILURE_FACT_MESSAGES.requestNotUnderstood); the server files it as a listing refusal.
  if (/couldn.t read the listing voolist sent/i.test(raw)) {
    return {
      title: `${label || 'The marketplace'} couldn't read this listing`,
      message: `${label || 'The marketplace'} couldn't read the listing Voolist sent.`,
      suggestion: 'Click Chat with us in the side menu, so we can fix it.',
      wayForward: 'contactVoolist',
      retryable: false,
    };
  }

  // ---- Vestiaire Collective. Matched on the extension's own sentences (chrome-extension/background/
  // marketplace-failures.js), and on the web app's for a create held back (createHeldBack).

  // Held back for now by the marketplace's pace between two new listings, or its daily limit: nothing was
  // sent, and the create waits in Voolist.
  if (/listings at least 2 minutes apart, so this one waits|new listings a day to .+\. This one waits/i.test(raw)) {
    return {
      title: `Waiting to go up on ${label || 'the marketplace'}`,
      message: raw,
      suggestion: 'Nothing was sent. Click Retry once the wait is over.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  // Voolist has paused new listings on the marketplace (its release switch is off): nothing was sent.
  if (/has paused sending new listings to/i.test(raw)) {
    return {
      title: `${label || 'The marketplace'} is paused in Voolist`,
      message: raw,
      suggestion: 'Nothing was sent. Click Retry later.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  // A listing Voolist sent "Remove from sale" still shows for sale, and Voolist never sends that twice: only the
  // seller can take it off sale now, on the marketplace. Voolist reads it again on later passes and lets go of the
  // listing once it is gone, so this row offers no Retry. Matched on the extension's own sentence
  // (marketplace-failures.js, withdrawNotConfirmed).
  if (/still shows this listing for sale\. please remove it from sale on .+ yourself/i.test(raw)) {
    const marketplace = label || 'the marketplace';
    return {
      title: `Remove it from sale on ${marketplace}`,
      message: `${label || 'The marketplace'} still shows this listing for sale.`,
      suggestion: `Please remove it from sale on ${marketplace} yourself, so it can't sell twice.`,
      wayForward: 'finishOnMarketplace',
      retryable: false,
    };
  }

  // An earlier try of this listing may have sent it for review, and Voolist can't tell which listing it became:
  // nothing is sent again on a guess, since the marketplace forbids two listings of one item. Customer service checks
  // with the seller, then links the listing or sends it.
  if (/may already have this item from an earlier try/i.test(raw)) {
    const marketplace = label || 'the marketplace';
    return {
      title: `It may already be on ${marketplace}`,
      message: `${label || 'The marketplace'} may already have this item from an earlier try that didn't finish, so Voolist didn't send it again.`,
      suggestion: "Click Chat with us in the side menu, and we'll sort it out with you.",
      wayForward: 'contactVoolist',
      retryable: false,
    };
  }

  // Only Voolist can fix these: which of the marketplace's sites the account sells on, or details its form asks
  // for that Voolist cannot send yet.
  if (/couldn.t tell which .+ site your account sells on/i.test(raw)) {
    return {
      title: `Voolist needs to know your ${label || 'marketplace'} site`,
      message: `Voolist couldn't tell which ${label || 'marketplace'} site your account sells on.`,
      suggestion: 'Click Chat with us in the side menu, so we can fix it.',
      wayForward: 'contactVoolist',
      retryable: false,
    };
  }
  if (/asks for details for this category that voolist can.t send yet/i.test(raw)) {
    return {
      title: `${label || 'The marketplace'} asks for more than Voolist can send`,
      message: raw.split(' Please reach out')[0],
      suggestion: 'Click Chat with us in the side menu, so we can add them.',
      wayForward: 'contactVoolist',
      retryable: false,
    };
  }

  // What the listing itself has to change first: its brand, a choice the marketplace's form needs, its photos,
  // its price. Retry alone sends the same listing, so none of these offers it.
  if (/doesn.t accept .+\. only brands on .+'s list can be listed/i.test(raw)) {
    return {
      title: `${label || 'The marketplace'} doesn't accept this brand`,
      message: raw,
      suggestion: 'If the brand is wrong, choose it from the list in the item\'s details, then try again.',
      wayForward: 'retry',
      retryable: false,
    };
  }
  if (/needs an? .+\. choose one in the item.s .+ details, then try again/i.test(raw)) {
    return {
      title: `Choose the item's details for ${label || 'the marketplace'}`,
      message: raw.split('. Choose one')[0].concat('.'),
      suggestion: `Choose one in the item's ${label || 'marketplace'} details, then try again.`,
      wayForward: 'retry',
      retryable: false,
    };
  }
  if (/needs at least \d+ photos|needs every photo to be at least|couldn.t read photo \d+ of this listing/i.test(raw)) {
    return {
      title: `${label || 'The marketplace'} needs other photos`,
      message: raw,
      suggestion: 'Change the listing\'s photos, then try again.',
      wayForward: 'retry',
      retryable: false,
    };
  }
  if (/lowest price is .+\. raise the price, then try again/i.test(raw)) {
    return {
      title: `Too low for ${label || 'the marketplace'}`,
      message: raw.split(' Raise the price')[0],
      suggestion: 'Raise the price, then try again.',
      wayForward: 'retry',
      retryable: false,
    };
  }

  // Stopped before anything was sent, for a reason a second try settles: the marketplace kept something else
  // than Voolist filled in (its own recommended price, say), Voolist couldn't save its progress, a list didn't
  // load, or the item's earlier listing couldn't be checked before posting it again.
  if (/didn.t keep the listing as voolist filled it in/i.test(raw)) {
    return {
      title: `${label || 'The marketplace'} changed the listing`,
      message: raw.split(' Please try again')[0],
      suggestion: 'Please try again.',
      wayForward: 'retry',
      retryable: true,
    };
  }
  if (/couldn.t save its progress while listing on|list of brands didn.t load/i.test(raw)) {
    return {
      title: `Couldn't finish listing on ${label || 'the marketplace'}`,
      message: raw.split(' Please try again')[0],
      suggestion: 'Nothing was sent. Please try again.',
      wayForward: 'retry',
      retryable: true,
    };
  }
  if (/couldn.t check this item.s earlier listing on/i.test(raw)) {
    const marketplace = label || 'the marketplace';
    return {
      title: `Check the earlier ${label || 'marketplace'} listing`,
      message: raw.split(' Check this listing')[0],
      suggestion: `Check this listing on ${marketplace}, then try again.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // Details the marketplace needs were missing, so nothing was sent; the sentence names them
  // (missingDetailsMessage in the extension).
  if (lower.includes('needs these details before it can take this listing')) {
    const marketplace = label || 'The marketplace';
    const details = raw.split(':').slice(1).join(':').split('.')[0].trim();
    return {
      title: `${marketplace} needs more details`,
      message: details ? `${marketplace} needs: ${details}.` : `${marketplace} needs more details for this listing.`,
      suggestion: `Add them in the item's ${marketplace} details, then try again.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // The marketplace refused the listing in its own words, which name the one thing to change
  // ("Size can't be blank."). The extension wraps them (refusedByMarketplaceMessage).
  if (/didn.t accept this listing:/i.test(raw)) {
    const marketplace = label || 'The marketplace';
    const theirWords = raw.slice(raw.indexOf(':') + 1).trim();
    return {
      title: `${marketplace} didn't accept the listing`,
      message: theirWords || `${marketplace} didn't accept this listing.`,
      suggestion: `Change that in the item's ${marketplace} details, then try again.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // The marketplace has asked for this item to be added by hand. Retrying sends the same listing
  // the same way and is refused again, so this row is deliberately NOT retryable: it is what takes
  // the Retry button off the row and the listing out of "Retry all failed". The post is left in the
  // seller's drafts on purpose, holding the photos and details they would otherwise re-enter.
  if (isBlockedAsAutomation(raw)) {
    const marketplace = label || 'The marketplace';
    return {
      title: `${marketplace} asked you to add this one yourself`,
      message: `${marketplace} sometimes asks for an item to be added by hand. Nothing is wrong with your listing.`,
      suggestion: `Your photos and details are already saved as a draft on ${label || 'the marketplace'}. Open it there to finish this item.`,
      wayForward: 'finishOnMarketplace',
      retryable: false,
    };
  }

  // Voolist holds no location for the seller's country, so the listing went out without one and
  // the marketplace refused it. Nothing about the listing or the account is wrong, and retrying
  // cannot help until Voolist adds the country — which is why this row is NOT retryable, and why
  // its advice sends the seller to us rather than back to the listing.
  if (COUNTRY_NOT_SUPPORTED_WORDING.test(raw)) {
    return {
      title: 'Not available in your country yet',
      message: `Voolist can't post to ${label || 'this marketplace'} in your country yet.`,
      suggestion: 'Click Chat with us in the side menu, so we can add your country.',
      wayForward: 'contactVoolist',
      retryable: false,
    };
  }

  // The extension uploads the photos to the marketplace and then asks it to check them. When that
  // check cannot reach the marketplace (its edge refused the call, which the browser can only report
  // as "Failed to fetch") or the marketplace takes too long to answer, nothing is wrong with the
  // seller's pictures: retrying fixes it. Checked before the generic timeout row, because a photo
  // check that timed out is a marketplace delay, not the extension failing to respond.
  if (lower.includes('picture validation failed')) {
    const marketplace = label || 'The marketplace';
    const cause = lower.includes('timed out')
      ? `${marketplace} took too long to answer.`
      : `Voolist couldn’t reach ${label || 'the marketplace'}.`;
    return {
      title: `${marketplace} didn’t finish checking the photos`,
      message: `${cause} Your photos are fine.`,
      suggestion: `Click Retry. If it keeps happening, open the ${label || 'marketplace'} website in another tab and try again.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  if (lower.includes('timeout') || lower.includes('timed out') || lower.includes("didn't respond")) {
    return {
      title: `${label || 'Voolist'} didn’t respond`,
      message: `${label || 'Voolist'} took too long to answer.`,
      suggestion: 'Try again and keep this tab open. If it keeps happening, close Chrome and open it again.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  // "busy right now" is how the server words a marketplace's rate limit (server/src/utils/error-parser.js).
  if (raw.includes('429') || lower.includes('rate limit') || lower.includes('too many requests') || lower.includes('busy right now')) {
    return {
      title: `${label || 'Voolist'} is busy`,
      message: `${label || 'Voolist'} is busy right now.`,
      suggestion: 'Wait a few minutes, then try again.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  if (raw.includes('413') || lower.includes('payload too large') || lower.includes('request entity too large')) {
    return {
      title: 'Listing too large to send',
      message: 'The photos or description are too large to send.',
      suggestion: 'Use fewer or smaller photos, or a shorter description, then try again.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  if (lower.includes('size is invalid') || lower.includes('size_id') || lower.match(/\bsize\b.*\b(invalid|required|missing)\b/)) {
    return {
      title: `Check the size for ${label || 'the marketplace'}`,
      message: `${label || 'The marketplace'} needs a size that fits this category.`,
      suggestion: 'Open the listing, choose a size from the list, then try again.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  if (lower.includes('invalid category') || lower.match(/category.*not.*support/) || lower.match(/category.*invalid/)) {
    return {
      title: `Check the category for ${label || 'the marketplace'}`,
      message: `${label || 'The marketplace'} doesn’t accept this category.`,
      suggestion: `Open the listing, choose a different ${label || 'marketplace'} category, then try again.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  if (lower.includes('package weight') || lower.match(/\bweight\b.*\b(required|missing|invalid)\b/)) {
    return {
      title: `${label || 'The marketplace'} needs a package weight`,
      message: 'This listing is missing a package weight.',
      suggestion: 'Open the listing, add the package weight, then try again.',
      wayForward: 'retry',
      retryable: false,
    };
  }

  if (lower.includes('image') && (lower.includes('required') || lower.includes('missing'))) {
    return {
      title: `${label || 'The marketplace'} needs photos`,
      message: `This listing has no photos ${label || 'the marketplace'} can use.`,
      suggestion: 'Open the listing, add photos, then try again.',
      wayForward: 'retry',
      retryable: false,
    };
  }

  // The seller ships on their own price and has not said what it is. This is fixed in Voolist, not
  // on the marketplace, so it is matched ahead of the "set up shipping" case below — a sentence
  // naming both would otherwise send the seller to the wrong place. Depop's own words for it, which
  // older extensions pass on unchanged, mean the same thing and read the same.
  if (lower.includes('shipping cost') || DEPOP_SHIPPING_NOT_NAMED.test(raw)) {
    return {
      title: `${label || 'The marketplace'} needs a shipping cost`,
      message: `${label || 'The marketplace'} needs to know what you charge for shipping before it will accept a listing.`,
      suggestion: `Add a shipping cost in the listing's ${label || 'marketplace'} section, then click Retry.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // The marketplace has no way to pay the seller yet, so it will post nothing for them. Same family
  // as the shipping-setup case below — the fix is on the marketplace and Retry only helps once it is
  // done — but it needs its own words, because "set up your shipping" would send them looking for a
  // setting that is not the problem.
  if (lower.includes('payment details') || lower.includes('payment info')) {
    return {
      title: `${label || 'The marketplace'} needs your payment details`,
      message: `${label || 'The marketplace'} won’t post a listing until your account is set up to get paid.`,
      suggestion: `Open ${label || 'the marketplace'}, add your payment info so you can get paid when you sell, then click Retry.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // The marketplace account has no address the seller ships from. Depop's postage labels are sent
  // from that address, and Depop refuses a US account with none the seller's own price too, so the
  // fix is on the marketplace — adding the address — and a shipping cost would not help. Matched on
  // the extension's own sentence (FAILURE_FACT_MESSAGES.shipFromAddressMissing in
  // chrome-extension/background/service-worker.js), which the server files with the account causes.
  if (lower.includes('needs your address')) {
    return {
      title: `${label || 'The marketplace'} needs your address`,
      // The listing form shows this line without the suggestion, so it names whose account it is.
      message: `${label || 'The marketplace'} won’t post a listing until your ${label ? `${label} ` : ''}account has the address you ship your items from.`,
      suggestion: `Open ${label || 'the marketplace'}, add the address you ship your items from, then click Retry.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // Every item on a local marketplace (OfferUp) is picked up somewhere, and neither the seller's account nor the
  // listing names a place, so nothing was posted. The seller fixes it in the marketplace's app or in the item's
  // section. Matched on the extension's own sentence (FAILURE_FACT_MESSAGES.pickupLocationMissing).
  if (lower.includes('needs a pickup location')) {
    const marketplace = label || 'the marketplace';
    return {
      title: `${label || 'The marketplace'} needs a pickup location`,
      message: `${label || 'The marketplace'} needs to know where buyers pick this item up.`,
      suggestion: `Add your location in the ${marketplace} app, or a pickup ZIP code in the item's ${marketplace} section, then click Retry.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // The marketplace's free posts for the month are used up in the item's category (OfferUp), and more are
  // bought in its phone app only. Nothing was posted and nothing was taken down. Matched on the extension's own
  // sentence (freePostsUsedUpMessage), whose category the message keeps.
  const freePostsUsedUp = raw.match(/used this month.s free .+ posts for (.+?)\. buy more/i);
  if (freePostsUsedUp) {
    const marketplace = label || 'marketplace';
    return {
      title: `No free ${marketplace} posts left`,
      message: `You've used this month's free ${marketplace} posts for ${freePostsUsedUp[1]}.`,
      suggestion: `Buy more posts in the ${marketplace} app, then click Retry.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // The marketplace account itself is missing something a listing needs. Nothing is wrong with the
  // listing, so the generic "open the listing and check the fields" advice would send the seller to
  // the wrong place: the fix is on the marketplace, and Retry only helps once it is done.
  if (lower.includes('shipping settings') || lower.includes('shipping set up')) {
    return {
      title: `${label || 'The marketplace'} needs shipping set up`,
      message: `${label || 'The marketplace'} won’t accept a listing until your account has shipping set up.`,
      suggestion: `Open ${label || 'the marketplace'}, set up your shipping, then click Retry.`,
      wayForward: 'retry',
      retryable: true,
    };
  }

  // Depop's validation errors come back as a bare 422 far more often than a descriptive
  // message, and the four fields below are responsible for nearly all of them.
  if (platform === 'depop' && raw.includes('422')) {
    return {
      title: 'Depop didn’t accept the listing',
      message: 'A required detail is missing or not valid.',
      suggestion: 'Check that Department, Group, Product Type and Size are filled in, then click Retry.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  if (raw.includes('Request failed with status code')) {
    return {
      title: `Couldn’t post to ${label || 'the marketplace'}`,
      message: 'Something went wrong.',
      suggestion: 'Try again. If it keeps happening, click Chat with us in the side menu.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  const jsonMessage = extractJsonMessage(raw);
  if (jsonMessage) {
    return {
      title: `Couldn’t post to ${label || 'the marketplace'}`,
      message: jsonMessage,
      suggestion: 'Open the listing or click Retry.',
      wayForward: 'retry',
      retryable: true,
    };
  }

  const cleaned = cleanRaw(raw) || 'Something went wrong.';
  return {
    title: `Couldn’t post to ${label || 'the marketplace'}`,
    message: cleaned,
    suggestion: 'Click Retry. If it keeps failing, open the listing.',
    wayForward: 'retry',
    retryable: true,
  };
}

/**
 * A failure as a screen that has no Retry button of its own tells it in one line: the reason, and
 * the advice too when the way forward is to tell Voolist — that advice is the only thing the seller
 * can do, and it is true on every screen.
 *
 * Other advice is left out. Advice about retrying was written for the bulk results and their Retry
 * button, and each of these screens says in its own words how to try again. Advice to finish the
 * item on the marketplace is left out because those same screens invite the seller to try again,
 * and a seller who did both would post the item twice.
 *
 * @param {{message: string, suggestion?: string, wayForward?: string}} problem - what
 *   mapPlatformError said about the failure
 * @returns {string}
 */
export function describeFailure(problem) {
  return problem.wayForward === 'contactVoolist' && problem.suggestion
    ? `${problem.message} ${problem.suggestion}`
    : problem.message;
}

// Voolist's own server answers 401 only when the seller's sign-in to Voolist has run out, never for a
// marketplace account, so a 401 from it asks the seller to sign in again rather than to reconnect.
const SIGNED_OUT_OF_VOOLIST = {
  title: 'Sign in again',
  message: 'Your sign-in may have expired. Refresh the page and sign in again if asked.',
  suggestion: 'Refresh the page, then try again.',
  wayForward: 'retry',
  retryable: true,
};

// Voolist's own server answers 403 when it won't do what was asked on the seller's Voolist account,
// never for a marketplace account: most often the seller's plan doesn't include it. The server says
// why in `message`, in words written for the seller (its `error` is a code for the app), and that is
// what the seller reads. Without one, the plan is the reason given, with where the plan is changed.
// Trying again changes nothing until the seller has changed their plan, which they do away from the
// screen that failed, so that screen offers no Try again (`retryable: false`); once the plan is
// changed, the seller tries again (`wayForward: 'retry'`).
const NOT_ON_YOUR_PLAN = {
  title: 'Not on your plan',
  message:
    "Your plan doesn't include this. To change your plan, go to Account, open the Billing tab and click Change plan & payment.",
  suggestion: null,
  wayForward: 'retry',
  retryable: false,
};

function refusedByVoolist(serverMessage) {
  if (!serverMessage) return { ...NOT_ON_YOUR_PLAN };
  return { ...NOT_ON_YOUR_PLAN, title: "Voolist couldn't do that", message: serverMessage };
}

/**
 * Map an Axios-style error from Voolist's own server to the same shape as mapPlatformError.
 *
 * A 401 or 403 is about the seller's Voolist account (their sign-in, their plan), so each has its own
 * answer. Any other status is folded into the raw text so the same ordered match table applies.
 * `platform` is threaded straight through so platform-specific rows (e.g. Depop 422) still match for
 * HTTP failures, not only for extension-reported ones.
 * @param {Object} error - an axios error (or anything error-shaped)
 * @param {string} [platform]
 */
export function mapHttpError(error, platform) {
  if (!error) return mapPlatformError('Something went wrong.', platform);
  if (error?.response?.status === 401) return { ...SIGNED_OUT_OF_VOOLIST };
  if (error?.response?.status === 403) return refusedByVoolist(error.response.data?.message);
  const raw = error?.response?.data?.error
    || error?.response?.data?.message
    || error?.message
    || String(error);
  const status = error?.response?.status;
  if (status) {
    return mapPlatformError(`Request failed with status code ${status} - ${raw}`, platform);
  }
  return mapPlatformError(raw, platform);
}
